import React, { useState, useEffect } from 'react';
import {
  Button, Col, Row, Input, Spin, Card,
  Form, notification, Checkbox, Switch, Typography,
  Upload, Tabs, Tooltip, Modal, Collapse,
} from 'antd';
import MaskedInput from 'react-text-mask';
import { useNavigate, useMatch } from 'react-router-dom';
import {
  LoadingOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { fetchCitiesByState } from '../../../dataSource/APILocalidadesIBGE';
import { fetchCepDetails } from '../../../dataSource/APIViacep';
import './ManageBranches.scss';
import {
  post, put, get, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { formatTelephone } from '../../../utils/format';
import { SessionExpiredException } from '../../../utils/errors';
import { getUserPermissions } from '../../../utils/users';
import {
  API_URL,
} from '../../../utils/constants';
import { backendResponseCodes } from '../../../utils/responseMessages';

interface Branch {
  id?: number | null,
  name: string,
  city: string,
  uf: string,
  state: string,
  address: string,
  complement: string | undefined,
  cep: string,
  phone: string,
  email: string | undefined,
  segment: string | undefined,
  lat: string | undefined,
  log: string | undefined,
  displayRequest: boolean,
  status: string,
}
interface ArrayImageType {
  file: {
    status: string,
    name: string,
  } | undefined;
  fileList: Array<object> | [];
}

const Branches = (): JSX.Element => {
  const [state, setState] = useState<boolean>(true);
  const navigate = useNavigate();
  const userPermissions = getUserPermissions();
  const routeMatchEdit = useMatch('/system/branches/:id/edit');
  const [mode] = useState(!routeMatchEdit ? 'CREATE' : 'EDIT');
  const [formAddInstance] = Form.useForm();
  const [formAttachInstance] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [checkView, setCheckView] = useState<boolean>(false);
  const [idBranchEditting, setIdBranchEditting] = useState<number>(0);
  const [cepApiLoading, setCepApiLoading] = useState<boolean>(false);
  const [arrDataCover, setArrDataCover] = useState<object[]>([]);
  const [arrDataImages, setArrDataImages] = useState<object[]>([]);
  const [previewImageCover, setPreviewImageCover] = useState<object[]>([]);
  const [previewAlbum, setPreviewAlbum] = useState<object[]>([]);
  const [changePhotos, setChangePhotos] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string>();
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewTitle, setPreviewTitle] = useState<string>('');

  const ValidatorRule = (_rule: any, value: []): Promise<void> => {
    if (value == undefined) {
      return Promise.resolve();
    }

    if (!value.length) {
      return Promise.resolve();
    }

    const allowedExtensions: string[] = [
      'png', 'jpg', 'jpeg',
    ];
    let typeError = false;
    let extension = value[value.length - 1]?.name.split('.');

    extension = extension[extension.length - 1];

    if (allowedExtensions.includes(extension) === false) {
      typeError = true;
    }

    if (typeError) {
      const msgError = 'Tipo de arquivo invalido. As extensões permitidas são: .png, .jpg e .jpeg';
      return Promise.reject(new Error(msgError));
    }
    return Promise.resolve();
  };

  /**
   * @name maskPhone
   * @param rawValue
   * @returns retorna o texto de telefone formatado para ser exibido
   */
  const maskPhone = (rawValue: string): (string | RegExp)[] => {
    if (rawValue.length > 14) {
      return [
        '(',
        /[1-9]/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ];
    }

    return [
      '(',
      /[1-9]/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ];
  };

  /**
   * @name handleStateAddressChange
   * @param stateValue
   * @descrition realiza a busca de estado/cidade baseado em endereço
   */
  const handleStateAddressChange = async (stateValue: string): Promise<void> => {
    formAddInstance.setFieldsValue({
      city: undefined,
    });

    let response;
    let responseError;

    try {
      const requestRef: RequestWrapper = fetchCitiesByState({
        identifier: stateValue,
      });

      response = await requestRef;
    } catch (error: unknown) {
      console.log('erro fetchCitiesByState', error);

      if (!(error instanceof SessionExpiredException)) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        }
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        // success
        break;

      case (responseError?.error):
      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar as cidades do Estado selecionado.',
        });
        break;
    }
  };

  /**
   * @name fetchCepData
   * @param cepValue
   * @returns consulta e retorna o endereço atrelado ao cep informado
   */
  const fetchCepData = async (cepValue: string): Promise<void> => {
    if (cepValue.length !== 8) {
      return;
    }

    let response;

    try {
      setCepApiLoading(true);
      const requestRef: RequestWrapper = fetchCepDetails({
        cep: cepValue,
        returnType: 'json',
      });

      response = await requestRef;

      switch (true) {
        case (response.statusCode === 200 && !('erro' in response.body)):
          formAddInstance.setFieldsValue({
            uf: response.body.uf,
            address: response.body.logradouro,
          });

          await handleStateAddressChange(response.body.uf);
          formAddInstance.setFieldsValue({
            city: response.body.localidade,
          });

          break;

        case (response.body.erro === true):
        default:
          break;
      }
    } catch (error: unknown) {
      console.log('viacep response error', error);
      return;
    } finally {
      setCepApiLoading(false);
    }
  };

  /**
   * @name validateCep
   * @param _rule
   * @param value
   * @returns retorna a validação do cep
   */
  const validateCep = async (_rule: any, value: string): Promise<Error | void> => {
    if (!value || value.length !== 8) {
      return Promise.resolve();
    }

    let response;

    try {
      setCepApiLoading(true);
      const requestRef: RequestWrapper = fetchCepDetails({
        cep: value,
        returnType: 'json',
      });

      response = await requestRef;

      switch (true) {
        case (response.statusCode === 200 && !('erro' in response.body)):
          return Promise.resolve();

        case (response.body.erro === true):
        default:
          return Promise.reject(new Error('CEP inválido'));
      }
    } catch (error: unknown) {
      console.log('viacep response error', error);
      return Promise.reject(new Error('Erro ao validar cep'));
    } finally {
      setCepApiLoading(false);
    }
  };

  /**
   * @name fetchBranchData
   * @param {*} id
   * @description busca a filial através do seu id para carregar suas informações dentro da modal
   * @returns retorna a modal com as informações da filial para edição
   */
  const fetchBranchData = async (id: number): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      setIdBranchEditting(id);

      const requestReference: RequestWrapper = get({
        url: `/branch/findById/${id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === backendResponseCodes
        .Success
        .Branch
        .FindByBranch
        .code
      ): {
          const dataCep: string = (
            response?.body?.details?.cep
              ? response?.body?.details?.cep.replaceAll('.', '').replaceAll('-', '')
              : ''
          );

          formAddInstance.setFieldsValue({
            name: response?.body?.details?.name,
            complement: response?.body?.details?.complement,
            city: response?.body?.details?.city,
            uf: response?.body?.details?.uf,
            // state: response?.body?.details?.state,
            address: response?.body?.details?.address,
            cep: dataCep,
            phone:
              formatTelephone(response?.body?.details?.phone),
            email: response?.body?.details?.email,
            segment: response?.body?.details?.segment,
            lat: response?.body?.details?.lat,
            log: response?.body?.details?.log,
            status: response?.body?.details?.status === 'ACTIVATED',
          });

          if (response?.body?.details?.branchFilesDetailsOutputs) {
            response?.body?.details?.branchFilesDetailsOutputs.map((i: {
              url: string;
              thumbUrl: string;
              urlImage: string;
            }) => {
              i.url = `${API_URL}${i?.urlImage}`;
              i.thumbUrl = `${API_URL}${i?.urlImage}`;
            });

            const convertCoverToFile = response?.body?.details?.branchFilesDetailsOutputs?.filter(
              (i: {
                coverImage: boolean;
              }) => {
                return i.coverImage === true;
              });

            convertCoverToFile.map(async (i: {
              name: string;
              type: string;
              coverImage: boolean;
              size: number;
              originFileObj: File;
              urlImage: string;
            }) => {
              fetch(API_URL + i.urlImage).then((data) => {
                data.blob().then((blob) => {
                  i.originFileObj = new File([blob], i.name, {
                    type: `image/${i.type}`,
                  });
                });
              });
            });

            const convertImagesToFile = response?.body?.details?.branchFilesDetailsOutputs?.filter(
              (i: {
                coverImage: boolean;
              }) => {
                return i.coverImage === false;
              });

            convertImagesToFile.map(async (i: {
              name: string;
              type: string;
              coverImage: boolean;
              size: number;
              originFileObj: File;
              urlImage: string;
            }) => {
              fetch(API_URL + i.urlImage).then((data) => {
                data.blob().then((blob) => {
                  i.originFileObj = new File([blob], i.name, {
                    type: `image/${i.type}`,
                  });
                });
              });
            });

            formAttachInstance.setFieldsValue({
              dataCover: convertCoverToFile,
              dataImages: convertImagesToFile,
            });

            setArrDataCover(convertCoverToFile);
            setPreviewImageCover(convertCoverToFile);
            setArrDataImages(convertImagesToFile);
            setPreviewAlbum(convertImagesToFile);
          }

          setCheckView(response?.body?.details?.displayRequest);
        }
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .YouNotPermissionOperation
        .code
      ):
        // You do not have permission to perform this operation, -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .YouNotPermissionOperation
            .description
            .pt_br,
        });
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .BranchNotFound
        .code):
        // Branch not found, -17000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .BranchNotFound
            .description
            .pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .Generic
            .description
            .pt_br,
        });
        break;
    }
  };

  /**
   * @name handleSubmit
   * @returns gerencia os dados para ser atualizado ou criar um novo registro
   */
  const handleSubmit = async (values: Branch): Promise<void> => {
    let response;
    let responseError;
    let genericError;
    setLoading(true);

    try {
      await formAddInstance.validateFields();

      if (mode === 'EDIT'
        && routeMatchEdit?.params?.id == null) {
        notification.error({
          message: 'Error',
          description: 'Desculpe, houve um erro ao realizar esta ação.',
        });

        navigate('/system/branches');
        return;
      }
    } catch (error: unknown) {
      setLoading(false);

      notification.warning({
        message: 'Atenção',
        description: 'Informações inválidas, verifique e tente novamente por favor',
      });

      return;
    }

    const dataCep = `${values.cep.substring(0, 2)}.${values.cep.substring(2, 5)}-${values.cep.substring(5)}`;
    const switchActiveBranch = values.status != undefined
      ? values.status
      : true;

    const dataRequest: Branch = {
      id: null,
      name: values.name.toUpperCase(),
      city: values.city,
      uf: values.uf,
      state: values.uf,
      address: values.address,
      complement: values.complement || undefined,
      cep: dataCep,
      phone: values.phone,
      email: values.email || undefined,
      segment: values.segment || undefined,
      lat: values.lat || undefined,
      log: values.log || undefined,
      displayRequest: checkView != undefined ? checkView : false,
      status: switchActiveBranch === true ? 'ACTIVATED' : 'DELETED',
    };

    if (mode === 'EDIT') {
      try {
        dataRequest.id = Number(routeMatchEdit?.params?.id);
        const requestReference: RequestWrapper = put({
          url: '/branch/editBranch',

          params: {
            id: dataRequest.id,
            name: dataRequest.name,
            city: dataRequest.city,
            uf: dataRequest.uf,
            state: dataRequest.uf,
            address: dataRequest.address,
            complement: dataRequest.complement,
            cep: dataRequest.cep,
            phone: dataRequest.phone,
            email: dataRequest.email,
            segment: dataRequest.segment,
            lat: dataRequest.lat,
            log: dataRequest.log,
            displayRequest: dataRequest.displayRequest,
            status: dataRequest.status,
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      setLoading(false);
    } else {
      try {
        const requestReference: RequestWrapper = post({
          url: '/branch/requestBranch',

          params: {
            name: dataRequest.name,
            city: dataRequest.city,
            uf: dataRequest.uf,
            state: dataRequest.uf,
            address: dataRequest.address,
            complement: dataRequest.complement,
            cep: dataRequest.cep,
            phone: dataRequest.phone,
            email: dataRequest.email,
            segment: dataRequest.segment,
            lat: dataRequest.lat,
            log: dataRequest.log,
            displayRequest: dataRequest.displayRequest,
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      setLoading(false);
    }

    switch (true) {
      case (response?.body?.code === backendResponseCodes
        .Success
        .Branch
        .BranchCreatedSuccessfully
        .code):
        // "Branch created Successfully", 16000
        notification.success({
          message: backendResponseCodes
            .Success
            .Branch
            .BranchCreatedSuccessfully
            .description
            .pt_br,
        });
        navigate('/system/branches');
        break;

      case (response?.body?.code === backendResponseCodes
        .Success
        .Branch
        .BranchEditedSuccessfully
        .code):
        // "Branch edited Successfully", 16000
        notification.success({
          message: backendResponseCodes
            .Success
            .Branch
            .BranchEditedSuccessfully
            .description
            .pt_br,
        });
        navigate('/system/branches');
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .YouNotPermissionOperation
        .code):
        // You do not have permission to perform this operation, -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .YouNotPermissionOperation
            .description
            .pt_br,
        });
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .BranchNotFound
        .code):
        // Branch not found, -17000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .BranchNotFound
            .description
            .pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .Generic
            .description
            .pt_br,
        });
        break;
    }
  };

  /**
   * @name handleSubmitAttach
   * @param values
   * @returns retorna o cadastro das imagens vinculadas a filial
   */
  const handleSubmitAttach = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    try {
      await formAttachInstance.validateFields();

      if (mode === 'EDIT'
        && routeMatchEdit?.params?.id == null) {
        notification.error({
          message: 'Error',
          description: 'Desculpe, houve um erro ao realizar esta ação.',
        });

        navigate('/system/branches');
        return;
      }
    } catch (error: unknown) {
      notification.warning({
        message: 'Atenção',
        description: 'Informações inválidas, verifique e tente novamente por favor',
      });

      return;
    }

    setLoading(true);
    try {
      if (routeMatchEdit?.params?.id != undefined) {
        const branchId = routeMatchEdit?.params?.id;
        const listAttaches = [...arrDataCover, ...arrDataImages];

        const url = '/file/uploadBranchImage';
        const formData = new FormData();
        listAttaches.forEach((file) => {
          formData.append('newAttachment', file['originFileObj']);
        });

        const requestReference: RequestWrapper = post({
          url,
          headers: {},
          params: formData,

          query: {
            branchId,
            editImage: true,
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } else {
        notification.error({
          message: 'ERRO',
          description: 'Desculpe, é necessário criar uma filial para anexar as imagens.',
        });
      }
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);
    setChangePhotos(false);

    switch (true) {
      case (response?.body?.code === backendResponseCodes
        .Success
        .Branch
        .UploadImageSuccessfully
        .code
      ):
        // Upload Successfully, 9020
        fetchBranchData(Number(routeMatchEdit?.params.id));
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes
            .Success
            .Branch
            .UploadImageSuccessfully
            .description
            .pt_br,
        });
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .NumberImagesExceeded
        .code
      ):
        // Number of images exceeded, -9020L
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .NumberImagesExceeded
            .description
            .pt_br,
        });
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .BranchNotFound
        .code):
        // Branch not found, -17000L
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .BranchNotFound
            .description
            .pt_br,
        });
        break;

      case (responseError?.body?.code === backendResponseCodes
        .Error
        .Branch
        .FileTypeNotSupported
        .code):
        // File type not supported, 9001L
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .FileTypeNotSupported
            .description
            .pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: backendResponseCodes
            .Error
            .Branch
            .Generic
            .description
            .pt_br,
        });
        break;
    }
  };

  /**
   * @name normFile
   * @param e
   * @returns retorna a normalização do arquivo
   */
  const normFile = (e: object): object => {
    if (Array.isArray(e)) {
      return e;
    }

    return e && e.fileList;
  };

  /**
   * @name handleChangeCover
   * @param arrChanges
   * @returns atualiza o estado que contém a lista de imagens para a capa da filial
   */
  const handleChangeCover = (arrChanges?: ArrayImageType): void => {
    setChangePhotos(true);
    setArrDataCover(arrChanges?.fileList.map((item: object) => ({ ...item })) as []);
  };

  /**
   * @name handleChangeImages
   * @param arrChanges
   * @returns atualiza o estado que contém a lista de imagens para a filial
   */
  const handleChangeImages = (arrChanges?: ArrayImageType): void => {
    setChangePhotos(true);
    if (arrChanges?.file?.status && arrChanges?.file?.status === 'removed') {
      // contexto para remover do estado
      const stayFiles = arrDataImages.filter((i: {
        name?: string;
      }) => { return i.name !== arrChanges?.file?.name; });
      setArrDataImages(stayFiles);
    } else {
      // contexto para adicionar no estado
      const arrJoinFiles: object[] | undefined = [];
      const arrNamesChosenFiles: Array<string | undefined> = [];

      // carrega todos os arquivos que possuem o atributo 'originFileObj' no value do form
      const arrChosenFiles = arrChanges?.fileList.filter((item: {
        originFileObj?: File;
      }): object | undefined => {
        if (item?.originFileObj) {
          return item;
        }
      });

      // carrega as imagens do estado que já existem para o join
      arrDataImages.forEach((item) => {
        arrJoinFiles.push(item);
      });

      // procura o nome de todas as imagens carregadas no value do form
      arrChosenFiles?.forEach((item: { name?: string; }): void => {
        arrNamesChosenFiles.push(item?.name);
      });

      // procura todos os nomes que já existam no estado de imagens
      const arrExistNamesFiles: Array<string | undefined> = [];
      arrDataImages.map((i: {
        name?: string;
      }): void => {
        if (arrNamesChosenFiles.includes(i?.name)) {
          arrExistNamesFiles.push(i?.name);
        }
      });

      // procura todos os índices/index do values do form dos nomes que já existam no estado
      const arrExistIndicesFiles: Array<number | undefined> = [];
      arrChosenFiles?.map((i: {
        name?: string;
      }, indice: number,
      ): void => {
        if (arrExistNamesFiles?.includes(i?.name)) {
          arrExistIndicesFiles.push(indice);
        }
      });

      // procura todoas as imagens que não existam com o índice no value do form
      const arrValidatedFiles = arrChosenFiles?.filter((i: object, indice: number) => {
        if (!arrExistIndicesFiles.includes(indice)) {
          return i;
        }
      });

      // carrega as imagens do value do form que não existam no estado para o join
      arrValidatedFiles?.forEach((i: object): void => { arrJoinFiles.push(i); });

      // finaliza o join
      setArrDataImages(arrJoinFiles);
    }
  };

  /**
   * @name getBase64
   * @param file
   * @returns retorna base64 da imagem para visualizar arquivo
   */
  const getBase64 = async (file: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (): unknown => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  /**
   * @name handlePreview
   * @param file
   * @returns retorna a visualização preview da imagem dentro da modal
   */
  const handlePreview = async (file: {
    url: string;
    preview: string | unknown;
    originFileObj: File;
    name: string;
  }): Promise<void> => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
    setPreviewVisible(true);
  };

  useEffect(() => {
    const executeOnMount = async (): Promise<void> => {
      if (
        mode === 'EDIT'
        && routeMatchEdit?.params?.id != null
      ) {
        await fetchBranchData(Number(routeMatchEdit.params.id));
      }
    };

    executeOnMount();

    return () => {
      setState(false);
    };
  }, []);

  return (
    <div className="ManageBranches">
      <Row
        className="ManageBranches__ComponentRow"
        gutter={[24, 24]}
      >
        <Col
          className="ManageBranches__ComponentColumn"
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
            className="ManageBranches__PageTitleRow"
          >
            <Col
              className="ManageBranches__PageTitleColumn"
            >
              <Typography.Title
                className="ManageBranches__PageTitle"
              >
                {
                  mode === 'CREATE'
                    ? 'Cadastrar filial'
                    : 'Editar filial'
                }
              </Typography.Title>
            </Col>

            <Col
              className="ManageBranches__ButtonColumn"
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 6 }}
              lg={{ span: 4 }}
              xl={{ span: 4 }}
            >
              <Row
                gutter={[24, 24]}
                justify="end"
                className="ManageBranches__NestedButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                >
                  <Button
                    block
                    type="default"
                    href="/system/branches"
                    className="ManageBranches__BackButton"
                    onClick={
                      (eventt): void => {
                        eventt.preventDefault();
                        navigate('/system/branches');
                      }
                    }
                  >
                    Voltar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row
            gutter={[24, 24]}
            className="ManageBranches__ContentRow"
          >
            <Col
              className="ManageBranches__ContentColumn"
              xs={{ span: 24 }}
            >

              <Tabs
                defaultActiveKey="1"
                className="ServiceDetail__Tabs"
              >
                <Tabs.TabPane
                  disabled={
                    changePhotos === true
                      ? true
                      : false
                  }
                  tab={(
                    <Typography.Title
                      level={5}
                      className="ServiceDetail__GeneralInfoTabTitle"
                    >
                      Informações gerais
                      {
                        changePhotos === true
                          ? (
                            <>
                              <span
                                style={{
                                  marginLeft: '10px',
                                }}
                              >
                                <Tooltip
                                  placement="right"
                                  title="Não é possível navegar de aba sem salvar as imagens."
                                >
                                  <InfoCircleOutlined />
                                </Tooltip>
                              </span>
                            </>
                          )
                          : null
                      }
                    </Typography.Title>
                  )}
                  key="1"
                >
                  <Card
                    className="ManageBranches__Card"
                  >

                    <Spin
                      tip="Carregando..."
                      spinning={loading}
                    >
                      <Form
                        autoComplete="on"
                        layout="vertical"
                        form={formAddInstance}
                        initialValues={{
                          status: true,
                        }}
                        onValuesChange={
                          (changedValues): void => {
                            if ('cep' in changedValues) {
                              fetchCepData(changedValues.cep);
                            }
                          }
                        }
                        onFinish={
                          async (formValues): Promise<void> => {
                            handleSubmit(formValues);
                          }
                        }
                      >
                        <Row gutter={[24, 24]}>
                          {
                            idBranchEditting > 0 && (userPermissions || []).includes('Inativação de filial')
                              ? (
                                <Col span={2}>
                                  <Form.Item
                                    label="Status"
                                    className="Branches__LabelForm"
                                    valuePropName="checked"
                                    name="status"
                                  >
                                    <Switch
                                      checkedChildren="Ativo"
                                      unCheckedChildren="Inativo"
                                    />
                                  </Form.Item>
                                </Col>
                              )
                              : null
                          }

                          <Col span={(idBranchEditting > 0 && (userPermissions || []).includes('Inativação de filial') ? 22 : 24)}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Nome"
                              name="name"
                              rules={[
                                { required: true, message: 'Por favor, insira um nome' },
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Nome"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="CEP"
                              name="cep"
                              rules={[
                                { required: true, message: 'Por favor, insira um cep' },
                                { len: 8, message: 'CEP deve ter exatamente 8 dígitos' },
                                { max: 8, message: 'Máximo de 8 caracteres' },
                                {
                                  validator: validateCep,
                                },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="CEP"
                                type="text"
                                maxLength={8}
                                addonAfter={
                                  cepApiLoading
                                    ? (<LoadingOutlined />)
                                    : null
                                }
                              />
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Endereço"
                              name="address"
                              rules={[
                                { required: true, message: 'Por favor, insira um endereço' },
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Endereço"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Cidade"
                              name="city"
                              rules={[
                                { required: true, message: 'Por favor, insira o nome da cidade' },
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Cidade"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="UF"
                              name="uf"
                              rules={[
                                { required: true, message: 'Por favor, insira o nome do estado' },
                                { min: 2, message: 'Mínimo de 2 caracteres' },
                                { max: 2, message: 'Máximo de 2 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="UF"
                                maxLength={2}
                              >
                              </Input>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Complemento"
                              name="complement"
                              rules={[
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Complemento"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Telefone"
                              name="phone"
                              rules={[
                                { required: true, message: 'Por favor, insira um telefone' },
                                { min: 14, message: 'Mínimo de 14 caracteres' },
                                { max: 15, message: 'Máximo de 15 caracteres' },
                              ]}
                            >
                              <MaskedInput
                                style={{ width: '100%' }}
                                guide={false}
                                mask={maskPhone}
                                placeholder="Telefone"
                                className="ant-input LocacaoPilotoSummary__PhoneMaskedInput"
                                maxLength={15}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="E-mail"
                              name="email"
                              rules={[
                                { max: 255, message: 'Máximo de 255 caracteres' },
                                {
                                  type: 'email',
                                  message: 'Formato de email inválido.',
                                },
                                {
                                  pattern: /^[A-z,0-9,@,.,_,-]+$/,
                                  message: 'Não utilize caracteres especiais, ou acentuados.',
                                },
                              ]}
                            >
                              <Input
                                type="email"
                                autoComplete="email"
                                aria-autocomplete="both"
                                style={{ width: '100%' }}
                                placeholder="E-mail"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Segmento"
                              name="segment"
                              rules={[
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Segmento"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Latitude"
                              name="lat"
                              rules={[
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Latitude"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              label="Longitude"
                              name="log"
                              rules={[
                                { max: 255, message: 'Máximo de 255 caracteres' },
                              ]}
                            >
                              <Input
                                style={{ width: '100%' }}
                                placeholder="Longitude"
                                maxLength={255}
                              >
                              </Input>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <Form.Item
                              className="Branches__LabelForm"
                              name="displayRequest"
                            >
                              <Checkbox
                                checked={checkView}
                                onChange={(e): void => {
                                  setCheckView(e.target.checked);
                                }}
                              >
                                Exibir filial na solicitação de serviços e orçamentos.
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>

                        {
                          arrDataCover.length > 0
                          && (
                            <Collapse defaultActiveKey={['0']}>
                              <Collapse.Panel header="Anexo de imagens" key="1">
                                <Row
                                  justify="space-between"
                                  style={{ marginBottom: 8 }}
                                  gutter={[24, 24]}
                                >
                                  <Col span={4}>
                                    <div>
                                      <h3 className="ManageBranches__PageTitleForm">Foto de capa da filial</h3>
                                      <Upload
                                        fileList={previewImageCover}
                                        onPreview={handlePreview}
                                        listType="picture-card"
                                        className="ServiceDetail__AddResultsInput"
                                        showUploadList={
                                          {
                                            showRemoveIcon: false,
                                          }
                                        }
                                      />
                                    </div>
                                  </Col>

                                  <Col span={20}>
                                    <div>
                                      <h3 className="ManageBranches__PageTitleForm">Fotos da filial</h3>
                                      {
                                        previewAlbum.length > 0
                                          ? (
                                            <Upload
                                              fileList={previewAlbum}
                                              onPreview={handlePreview}
                                              listType="picture-card"
                                              className="ServiceDetail__AddResultsInput"
                                              showUploadList={
                                                {
                                                  showRemoveIcon: false,
                                                }
                                              }
                                            />
                                          )
                                          : (
                                            <span
                                              style={{
                                                fontSize: '14px',
                                                fontStyle: 'italic',
                                              }}
                                            >
                                              Nenhuma imagem cadastrada para as fotos da filial.
                                            </span>
                                          )
                                      }
                                    </div>
                                  </Col>
                                </Row>
                              </Collapse.Panel>
                            </Collapse>
                          )
                        }

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                          {
                            (userPermissions || []).includes('Solicitar nova filial')
                              ? (
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                >
                                  Salvar
                                </Button>
                              )
                              : ''
                          }
                        </div>
                      </Form>
                    </Spin>
                  </Card>
                </Tabs.TabPane>

                <Tabs.TabPane
                  disabled={routeMatchEdit?.params?.id !== undefined ? false : true}
                  tab={(
                    <Typography.Title
                      level={5}
                      className="ServiceDetail__GeneralInfoTabTitle"
                    >
                      Importação de imagens
                      {
                        routeMatchEdit?.params?.id === undefined
                          ? (
                            <>
                              <span
                                style={{
                                  marginLeft: '10px',
                                }}
                              >
                                <Tooltip
                                  placement="right"
                                  title="Para anexar as imagens é necessário cadastrar uma filial."
                                >
                                  <InfoCircleOutlined />
                                </Tooltip>
                              </span>
                            </>
                          )
                          : null
                      }
                    </Typography.Title>
                  )}
                  key="2"
                >
                  <Card
                    className="ManageBranches__Card"
                  >

                    <Spin
                      tip="Carregando..."
                      spinning={loading}
                    >
                      <Form
                        autoComplete="on"
                        layout="vertical"
                        form={formAttachInstance}
                        initialValues={{
                          status: true,
                        }}
                        onFinish={
                          async (formValues): Promise<void> => {
                            handleSubmitAttach(formValues);
                          }
                        }
                      >
                        <Row gutter={[24, 24]}>
                          <Col span={12}>
                            <h3 className="ManageBranches__PageTitleForm">Foto de capa da filial</h3>
                            <Form.Item
                              name="dataCover"
                              getValueFromEvent={normFile}
                              valuePropName="fileList"
                              rules={[
                                { required: (arrDataImages.length > 0 ? true : false), message: 'Por favor, a capa é obrigatória para enviar as imagens.' },
                                {
                                  validator: ValidatorRule,
                                },
                              ]}
                            >
                              <Upload
                                onPreview={handlePreview}
                                maxCount={1}
                                name="dataCover"
                                listType="picture-card"
                                className="ServiceDetail__AddResultsInput"
                                beforeUpload={
                                  (): boolean => {
                                    // override default upload and do it manually
                                    return false;
                                  }
                                }
                                onChange={handleChangeCover}
                              >
                                <Button
                                  icon={<PlusOutlined />}
                                  type="link"
                                  className="ServiceDetail__AddResultsButton"
                                  block
                                >
                                  Adicionar uma capa
                                </Button>
                              </Upload>
                            </Form.Item>
                          </Col>

                          <Col span={12}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                              }}
                            >
                              <h3 className="ManageBranches__PageTitleForm">
                                Fotos da filial
                              </h3>

                              {
                                arrDataCover.length == 0 && arrDataImages.length > 0
                                  ? (
                                    <span
                                      style={{
                                        marginLeft: '10px',
                                      }}
                                    >
                                      <Tooltip
                                        placement="right"
                                        title="Para continuar adicionando ou removendo as fotos da filial é necessário carregar uma capa primeiro."
                                      >
                                        <InfoCircleOutlined />
                                      </Tooltip>
                                    </span>
                                  )
                                  : null
                              }
                            </div>
                            <Form.Item
                              name="dataImages"
                              getValueFromEvent={normFile}
                              valuePropName="fileList"
                              rules={[
                                {
                                  validator: ValidatorRule,
                                },
                              ]}
                            >
                              <Upload
                                multiple
                                onPreview={handlePreview}
                                disabled={arrDataCover.length > 0 ? false : true}
                                maxCount={3}
                                name="dataImages"
                                listType="picture-card"
                                className="ServiceDetail__AddResultsInput"
                                beforeUpload={
                                  (): boolean => {
                                    // override default upload and do it manually
                                    return false;
                                  }
                                }
                                onChange={handleChangeImages}
                              >
                                {
                                  arrDataCover.length > 0
                                    ? (
                                      <>
                                        <Button
                                          icon={<PlusOutlined />}
                                          type="link"
                                          className="ServiceDetail__AddResultsButton"
                                          block
                                        >
                                          Adicionar fotos
                                        </Button>
                                      </>
                                    )
                                    : (
                                      <>
                                        <Button
                                          disabled
                                          icon={<PlusOutlined />}
                                          type="link"
                                          className="ServiceDetail__AddResultsButton"
                                          block
                                        >
                                          Adicionar fotos
                                        </Button>
                                      </>
                                    )
                                }

                              </Upload>
                            </Form.Item>
                          </Col>
                        </Row>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {
                            (userPermissions || []).includes('Solicitar nova filial')
                              ? (
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                >
                                  Salvar
                                </Button>
                              )
                              : ''
                          }
                        </div>
                      </Form>

                    </Spin>
                  </Card>
                </Tabs.TabPane>
              </Tabs>

              <Modal
                visible={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={(): void => {
                  setPreviewVisible(false);
                }}
              >
                <img alt="imagens da filial" style={{ width: '100%' }} src={previewImage} />
              </Modal>

            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

Branches.propTypes = {};
Branches.defaultProps = {};

export default Branches;
